import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const microeconomicsUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Supply and Demand",
  examWeight: "20–25%",
  focus: "How market prices are determined, why they change, and the impact of government intervention and international trade.",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Demand",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 The Law of Demand</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Law of Demand states that there is an <strong>inverse relationship</strong> between the price of a good and the quantity demanded, <em>ceteris paribus</em> (all other things held constant).
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4 text-center">
              <div className="flex justify-center items-center gap-8 text-lg font-semibold text-blue-900">
                <span><InlineMath math="P \uparrow \implies Q_d \downarrow" /></span>
                <div className="h-8 w-px bg-blue-300"></div>
                <span><InlineMath math="P \downarrow \implies Q_d \uparrow" /></span>
              </div>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-2">Graphing Demand:</h4>
              <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                <li>The demand curve is <strong>downward sloping</strong>.</li>
                <li><strong>Y-axis:</strong> Price (<InlineMath math="P" />)</li>
                <li><strong>X-axis:</strong> Quantity (<InlineMath math="Q" />)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">❓ Why is Demand Downward Sloping?</h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border-l-4 border-indigo-500 shadow-sm">
                <h4 className="font-bold text-indigo-900">1. The Income Effect</h4>
                <p className="text-sm text-slate-700">As the price of a good falls, a consumer’s purchasing power (real income) increases, allowing them to purchase more of the good (and vice versa).</p>
              </div>
              <div className="bg-white p-4 rounded-lg border-l-4 border-purple-500 shadow-sm">
                <h4 className="font-bold text-purple-900">2. The Substitution Effect</h4>
                <p className="text-sm text-slate-700">As the price of a good rises, consumers switch to relatively cheaper substitutes. As price falls, consumers switch away from other goods to the now cheaper good.</p>
              </div>
              <div className="bg-white p-4 rounded-lg border-l-4 border-pink-500 shadow-sm">
                <h4 className="font-bold text-pink-900">3. Diminishing Marginal Utility</h4>
                <p className="text-sm text-slate-700">As a consumer consumes more units of a specific good, the additional satisfaction (utility) gained from each extra unit declines. Therefore, consumers are only willing to buy additional units if the price is lower.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Change in Quantity Demanded vs. Change in Demand</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Change in Quantity Demanded (<InlineMath math="\Delta Q_d" />)</h4>
                <ul className="mt-2 text-sm text-slate-700 space-y-1">
                  <li><strong>Cause:</strong> Change in the <strong>own-price</strong> of the good.</li>
                  <li><strong>Visual:</strong> Movement <strong>along</strong> the existing curve.</li>
                </ul>
              </div>
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">Change in Demand (<InlineMath math="\Delta D" />)</h4>
                <ul className="mt-2 text-sm text-slate-700 space-y-1">
                  <li><strong>Cause:</strong> Change in a <strong>non-price determinant</strong>.</li>
                  <li><strong>Visual:</strong> The entire curve <strong>shifts</strong> (Left = Decrease; Right = Increase).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 Determinants of Demand (Shifters)</h3>
            <Mnemonic title="TRIBE" content="Tastes, Related Goods, Income, Buyers, Expectations" />
            <div className="mt-4 space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Related Goods (Prices of):</h4>
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                  <div className="bg-white p-3 rounded border border-slate-200">
                    <p className="text-xs font-bold text-blue-800">Substitutes</p>
                    <p className="text-xs mt-1">Goods used in place of one another. If <InlineMath math="P_{Coke} \uparrow, D_{Pepsi} \uparrow" /> (Direct relationship).</p>
                  </div>
                  <div className="bg-white p-3 rounded border border-slate-200">
                    <p className="text-xs font-bold text-red-800">Complements</p>
                    <p className="text-xs mt-1">Goods used together. If <InlineMath math="P_{PB} \uparrow, D_{Jelly} \downarrow" /> (Inverse relationship).</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Income:</h4>
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                  <div className="bg-white p-3 rounded border border-slate-200">
                    <p className="text-xs font-bold text-green-800">Normal Goods</p>
                    <p className="text-xs mt-1">As Income <InlineMath math="\uparrow" />, Demand <InlineMath math="\uparrow" /> (e.g., steak, new cars).</p>
                  </div>
                  <div className="bg-white p-3 rounded border border-slate-200">
                    <p className="text-xs font-bold text-orange-800">Inferior Goods</p>
                    <p className="text-xs mt-1">As Income <InlineMath math="\uparrow" />, Demand <InlineMath math="\downarrow" /> (e.g., instant noodles, used cars).</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Supply",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 The Law of Supply</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Law of Supply states that there is a <strong>direct (positive) relationship</strong> between the price of a good and the quantity supplied, <em>ceteris paribus</em>.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4 text-center">
              <div className="flex justify-center items-center gap-8 text-lg font-semibold text-green-900">
                <span><InlineMath math="P \uparrow \implies Q_s \uparrow" /></span>
                <div className="h-8 w-px bg-green-300"></div>
                <span><InlineMath math="P \downarrow \implies Q_s \downarrow" /></span>
              </div>
            </div>
            <KeyTerm 
              term="Profit Incentive" 
              definition="Higher prices provide a profit motive for firms. Additionally, as production expands, marginal costs rise, requiring higher prices to cover costs."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ Determinants of Supply (Shifters)</h3>
            <Mnemonic title="ROTTEN" content="Resources, Other goods, Taxes/Subsidies, Technology, Expectations, Number of sellers" />
            <div className="grid md:grid-cols-2 gap-3 mt-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                <h4 className="font-bold text-red-900 text-sm">Resource/Input Prices</h4>
                <p className="text-xs text-slate-700 mt-1">If labor or raw materials cost more, production is more expensive and supply decreases (shifts left).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h4 className="font-bold text-blue-900 text-sm">Technology</h4>
                <p className="text-xs text-slate-700 mt-1">Improvements increase productivity and lower costs, shifting supply to the right.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 md:col-span-2">
                <h4 className="font-bold text-yellow-900 text-sm">Taxes and Subsidies</h4>
                <p className="text-xs text-slate-700 mt-1"><strong>Tax:</strong> Treated as an extra cost (Supply <InlineMath math="\downarrow" />). <strong>Subsidy:</strong> Financial aid from government (Supply <InlineMath math="\uparrow" />).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Price Elasticity of Demand",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📏 Definition and Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Price Elasticity of Demand (<InlineMath math="E_d" />) measures the responsiveness of the quantity demanded to a change in price.</p>
            <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
              <BlockMath math="E_d = \frac{\% \Delta \text{Quantity Demanded}}{\% \Delta \text{Price}}" />
              <p className="text-xs text-center text-slate-500 mt-2">Note: Economists typically use the absolute value as it is always negative.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Categories of Elasticity</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white p-3 rounded border border-slate-200">
                  <p className="font-bold text-slate-800 text-sm">Elastic (<InlineMath math="|E_d| > 1" />)</p>
                  <p className="text-xs text-slate-600">Quantity is very sensitive to price. <InlineMath math="\% \Delta Q_d > \% \Delta P" />. (e.g., Luxuries)</p>
                </div>
                <div className="bg-white p-3 rounded border border-slate-200">
                  <p className="font-bold text-slate-800 text-sm">Inelastic (<InlineMath math="|E_d| < 1" />)</p>
                  <p className="text-xs text-slate-600">Quantity is insensitive to price. <InlineMath math="\% \Delta Q_d < \% \Delta P" />. (e.g., Insulin)</p>
                </div>
              </div>
              <div className="bg-indigo-50 p-3 rounded border border-indigo-200">
                <p className="text-xs text-indigo-900 italic"><strong>Extreme cases:</strong> Perfectly Inelastic (<InlineMath math="E_d = 0" />, Vertical curve); Perfectly Elastic (<InlineMath math="E_d = \infty" />, Horizontal curve).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Total Revenue Test</h3>
            <p className="text-sm text-slate-700 mb-4">Total Revenue (<InlineMath math="TR" />) = <InlineMath math="\text{Price} \times \text{Quantity}" />.</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-orange-50 p-4 rounded border border-orange-200 text-center">
                <h4 className="font-bold text-orange-900">Elastic</h4>
                <p className="text-xs mt-1"><InlineMath math="P \uparrow, TR \downarrow" /></p>
                <p className="text-xs text-slate-500 italic">(Opposite directions)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded border border-blue-200 text-center">
                <h4 className="font-bold text-blue-900">Inelastic</h4>
                <p className="text-xs mt-1"><InlineMath math="P \uparrow, TR \uparrow" /></p>
                <p className="text-xs text-slate-500 italic">(Same direction)</p>
              </div>
              <div className="bg-slate-50 p-4 rounded border border-slate-200 text-center">
                <h4 className="font-bold text-slate-900">Unit Elastic</h4>
                <p className="text-xs mt-1"><InlineMath math="\Delta TR = 0" /></p>
                <p className="text-xs text-slate-500 italic">(TR maximized)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Price Elasticity of Supply",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ Price Elasticity of Supply (Es)</h3>
            <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="E_s = \frac{\% \Delta \text{Quantity Supplied}}{\% \Delta \text{Price}}" />
            </div>
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-800">Primary Determinant: Time</h4>
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                <li><strong>Short Run:</strong> Supply is inelastic (hard to build new factories quickly).</li>
                <li><strong>Long Run:</strong> Supply becomes more elastic as all inputs become variable.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Other Elasticities",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Cross-Price Elasticity of Demand (Exy)</h3>
            <p className="text-sm text-slate-700 mb-4">Measures how the quantity demanded of Good X responds to a price change in Good Y.</p>
            <div className="bg-pink-50 p-4 rounded-lg border border-pink-200 mb-4">
              <BlockMath math="E_{xy} = \frac{\% \Delta Q_d \text{ of Good X}}{\% \Delta \text{Price of Good Y}}" />
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-white p-2 rounded text-center">
                  <p className="text-xs font-bold text-green-700">Positive (+)</p>
                  <p className="text-xs">Substitutes</p>
                </div>
                <div className="bg-white p-2 rounded text-center">
                  <p className="text-xs font-bold text-red-700">Negative (-)</p>
                  <p className="text-xs">Complements</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💵 Income Elasticity of Demand (Ei)</h3>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <BlockMath math="E_i = \frac{\% \Delta Q_d}{\% \Delta \text{Income}}" />
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-white p-2 rounded text-center">
                  <p className="text-xs font-bold text-green-700">Positive (+)</p>
                  <p className="text-xs">Normal Good</p>
                </div>
                <div className="bg-white p-2 rounded text-center">
                  <p className="text-xs font-bold text-red-700">Negative (-)</p>
                  <p className="text-xs">Inferior Good</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Market Equilibrium and Surplus",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Equilibrium and Total Welfare</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Market Equilibrium occurs where <InlineMath math="Q_d = Q_s" />. This intersection defines <InlineMath math="P_e" /> (Price) and <InlineMath math="Q_e" /> (Quantity).</p>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-bold text-blue-900">Consumer Surplus (CS)</h4>
                <p className="text-sm text-slate-700">Difference between what a consumer is <strong>willing to pay</strong> and what they <strong>actually pay</strong>. Graphically: Below demand, above price.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="font-bold text-green-900">Producer Surplus (PS)</h4>
                <p className="text-sm text-slate-700">Difference between the <strong>market price</strong> and the <strong>minimum price</strong> accepted (Marginal Cost). Graphically: Above supply, below price.</p>
              </div>
            </div>
            
            <ImportantNote>
              <strong>Allocative Efficiency:</strong> Total Surplus (<InlineMath math="CS + PS" />) is maximized at equilibrium. At this point, <InlineMath math="MB (Demand) = MC (Supply)" />.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Market Disequilibrium and Changes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Disequilibrium</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-red-200 bg-red-50">
                <h4 className="font-bold text-red-900">Surplus</h4>
                <p className="text-xs mt-1"><InlineMath math="P > P_e" />. <InlineMath math="Q_s > Q_d" />. Downward pressure on price.</p>
              </div>
              <div className="p-4 rounded-lg border border-orange-200 bg-orange-50">
                <h4 className="font-bold text-orange-900">Shortage</h4>
                <p className="text-xs mt-1"><InlineMath math="P < P_e" />. <InlineMath math="Q_d > Q_s" />. Upward pressure on price.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">♊ Double Shifts (Simultaneous)</h3>
            <p className="text-sm text-slate-700 mb-4">When both curves shift, one variable is determinate and the other is <strong>indeterminate</strong> (ambiguous) without numerical data.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-slate-300 rounded-lg overflow-hidden text-sm">
                <thead className="bg-slate-100 font-bold">
                  <tr>
                    <th className="px-4 py-2 border">Scenario</th>
                    <th className="px-4 py-2 border">Price (P)</th>
                    <th className="px-4 py-2 border">Quantity (Q)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2 border font-medium"><InlineMath math="D\uparrow, S\uparrow" /></td>
                    <td className="px-4 py-2 border text-amber-600 font-bold">Indeterminate</td>
                    <td className="px-4 py-2 border text-green-700"><InlineMath math="\uparrow" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border font-medium"><InlineMath math="D\downarrow, S\downarrow" /></td>
                    <td className="px-4 py-2 border text-amber-600 font-bold">Indeterminate</td>
                    <td className="px-4 py-2 border text-red-700"><InlineMath math="\downarrow" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border font-medium"><InlineMath math="D\uparrow, S\downarrow" /></td>
                    <td className="px-4 py-2 border text-green-700"><InlineMath math="\uparrow" /></td>
                    <td className="px-4 py-2 border text-amber-600 font-bold">Indeterminate</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border font-medium"><InlineMath math="D\downarrow, S\uparrow" /></td>
                    <td className="px-4 py-2 border text-red-700"><InlineMath math="\downarrow" /></td>
                    <td className="px-4 py-2 border text-amber-600 font-bold">Indeterminate</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: Government Intervention",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛑 Price Controls</h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Price Ceilings (Legal Max)</h4>
                <ul className="text-sm text-slate-700 mt-1 space-y-1">
                  <li><strong>Binding:</strong> Set <strong>below</strong> equilibrium.</li>
                  <li><strong>Result:</strong> Shortage.</li>
                  <li><strong>Inefficiency:</strong> Creates Deadweight Loss (DWL).</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Price Floors (Legal Min)</h4>
                <ul className="text-sm text-slate-700 mt-1 space-y-1">
                  <li><strong>Binding:</strong> Set <strong>above</strong> equilibrium (e.g., Minimum Wage).</li>
                  <li><strong>Result:</strong> Surplus.</li>
                  <li><strong>Inefficiency:</strong> Creates Deadweight Loss.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💸 Taxes and Subsidies</h3>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Excise Taxes</h4>
                <p className="text-sm text-slate-700">Per-unit tax shifts the Supply curve vertically upward by the tax amount.</p>
                <div className="mt-2 grid grid-cols-2 gap-4 text-xs">
                  <div className="bg-white p-2 rounded"><strong>Price paid by consumers (<InlineMath math="P_c" />)</strong> increases.</div>
                  <div className="bg-white p-2 rounded"><strong>Price received by producers (<InlineMath math="P_p" />)</strong> decreases.</div>
                </div>
              </div>
              <KeyTerm 
                term="Tax Incidence" 
                definition="Who pays the tax? If Demand is more inelastic than Supply, consumers pay more. If Supply is more inelastic, producers pay more."
              />
              <KeyTerm 
                term="Deadweight Loss (DWL)" 
                definition="The loss of total surplus (efficiency) resulting from a market distortion like a tax. Represented by a triangle pointing toward equilibrium."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: International Trade and Policy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 World Price (<InlineMath math="P_w" />) vs Domestic Price</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Importing (<InlineMath math="P_w < P_{\text{Domestic}}" />)</h4>
                <ul className="text-sm text-slate-700 mt-1 list-disc pl-5">
                  <li>Gap between <InlineMath math="Q_d" /> and <InlineMath math="Q_s" /> is filled by <strong>Imports</strong>.</li>
                  <li><strong>Welfare:</strong> CS increases, PS decreases, Total Surplus increases.</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Exporting (<InlineMath math="P_w > P_{\text{Domestic}}" />)</h4>
                <ul className="text-sm text-slate-700 mt-1 list-disc pl-5">
                  <li>Surplus production is <strong>Exported</strong>.</li>
                  <li><strong>Welfare:</strong> PS increases, CS decreases, Total Surplus increases.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ Trade Barriers</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded border border-slate-200">
                <h4 className="font-bold text-slate-900">Tariffs</h4>
                <p className="text-xs text-slate-700 mt-1">Tax on imports. Raises effective price to <InlineMath math="P_w + T" />. Generates <strong>Government Revenue</strong> and DWL.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded border border-slate-200">
                <h4 className="font-bold text-slate-900">Quotas</h4>
                <p className="text-xs text-slate-700 mt-1">Physical limit on imports. Similar results to tariffs but generates no revenue for the government.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default microeconomicsUnit2Notes;